import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { currenPositionAutoScrollState } from '~/state/atoms/viewerAtoms';
import { sliceSweepOf, structureOf } from './sliceSweep';
import { useSliceSweepTarget } from './useSliceSweepTarget';

/** The slider's span along the axis, in patient mm, and the value it starts at. */
interface SliderRange {
    min: number;
    max: number;
    value: number;
}

/** The span a slider shows while it has no structure to scrub through. */
const IDLE_RANGE: SliderRange = { min: 0, max: 100, value: 0 };

/**
 * Scrubs the displayed slice through a structure along the sweep axis: the
 * slider spans the structure's extent along the axis, in patient mm, low to
 * high, and a change moves the plane intersection point of the sweep target
 * to that value. The target is the Environment that holds the active
 * structure set, so every view of it, in any row, follows. The extent is
 * read from the structure's resident texture and never builds one. While
 * there is nothing to scrub (no target, no view of it, or a structure
 * without an extent along the axis) the slider is not enabled, shows an
 * idle span and a change moves nothing.
 *
 * @param structure The ROI number of the structure to scrub through.
 * @returns Whether the slider is enabled, its value and span, the change
 * handler, and the value a patient point reads as on the axis.
 */
export const useSlider = (structure: number) => {
    const [currentPosition, setCurrentPosition] = useRecoilState(
        currenPositionAutoScrollState
    );
    const { environment, axis, shown } = useSliceSweepTarget();
    const roi = structureOf(environment, structure);
    const builtTexture = roi?.builtTexture;
    const [range, setRange] = useState<SliderRange | null>(null);

    const valueAlongAxis = useCallback(
        (position: ReadonlyVec3): number =>
            axis ? vec3.dot(position, axis) : 0,
        [axis]
    );

    useEffect(() => {
        const sweep = sliceSweepOf(roi, axis, builtTexture);
        if (!sweep) {
            setRange(null);
            return;
        }
        const low = valueAlongAxis(sweep.start);
        const high = valueAlongAxis(sweep.end);
        setRange({
            min: Math.min(low, high),
            max: Math.max(low, high),
            value: (low + high) / 2,
        });
        setCurrentPosition(vec3.clone(sweep.start));
    }, [roi, axis, builtTexture, valueAlongAxis, setCurrentPosition]);

    const enabled = range !== null && shown;

    const handleChange = useCallback(
        (_event: unknown, newValue: number | number[]) => {
            if (!enabled || !environment || !axis || !currentPosition) {
                return;
            }
            const value = Array.isArray(newValue) ? newValue[0] : newValue;
            const newPosition = vec3.scaleAndAdd(
                vec3.create(),
                currentPosition,
                axis,
                value - vec3.dot(currentPosition, axis)
            );
            environment.updatePlaneIntersectionPoint(newPosition);
            setRange((previous) =>
                previous ? { ...previous, value } : previous
            );
        },
        [enabled, environment, axis, currentPosition]
    );

    const {
        min: minPosition,
        max: maxPosition,
        value: sliderValue,
    } = range ?? IDLE_RANGE;

    return {
        enabled,
        sliderValue,
        handleChange,
        minPosition,
        maxPosition,
        valueAlongAxis,
    };
};
